import { createBrowserHistory } from "history";
import { configureStore } from "@reduxjs/toolkit";
import { AsyncEncryptStorage } from "encrypt-storage";
import { persistStore, persistReducer } from "redux-persist";
import promise from "redux-promise-middleware";
import createRootReducer from "./reducers";
import defaults from "./reducers/defaults";
import LogRocket from "logrocket";
import thunk from "redux-thunk";
import { generateMnemonic } from "../utilities";

export const SALT_KEY = "eleplan_salt"; // Define a key for localStorage
let salt = localStorage.getItem(SALT_KEY);
// Check if a salt exists in localStorage
let config = {
  prefix: "eleplan_db",
  storageType: "localStorage",
  stateManagementUse: true,
  //notifyHandler: (params) => process.env.REACT_APP_LOCAL && console.log("ENCRYPTION STATE", params),
  doNotEncryptValues: !salt
};

// If no salt exists, generate one and save it
if (!salt) {
  salt = generateMnemonic();
  localStorage.setItem(SALT_KEY, salt);
  config.doNotEncryptValues = true;
}
let encryptStorage = new AsyncEncryptStorage(salt, config);


// Set up initial persist config, using unencrypted storage by default
let persistConfig = {
  key: "eleplan",
  storage: encryptStorage,
  timeout: null,
  // Blacklist large data reducers to avoid localStorage quota issues
  // These can be reloaded from API on app load
  blacklist: [
    "router",
    "documents",      // Can contain large files/PDFs
    "prompts",        // Chat/query history can be very large
    "responses",      // Response data
    "notes",          // Many notes with content
    "messages",       // SMS messages
    "fasten",         // Health records can be huge
    "calendar",       // Calendar events
    "admin",          // Admin data
    "segments",       // Survey segments
    "blocks",         // Survey blocks
    "coverage",       // Coverage data
    "smsGroups",       // SMS group data
    "projectCreationDraft"  // Session-only; cleared on completion
  ],
};

const history = createBrowserHistory({ basename: "/" });

// Set up persisted reducer with initial config
let persistedReducer = persistReducer(persistConfig, createRootReducer(history));

// Sanitize sensitive data before it reaches LogRocket's servers.
// The localStorage snapshot is encrypted by encrypt-storage, but LogRocket's
// redux middleware receives the raw in-memory state. This sanitizer strips
// auth tokens, billing data, personal identifiers, and PII record lists,
// replacing them with safe placeholders so session replay is still useful
// for debugging UI/UX issues without leaking sensitive data.
const sanitizeStateForLogRocket = (state) => {
  if (!state) return state;
  const redactAccount = (account) => {
    if (!account) return account;
    return {
      ...account,
      cognito_id: '[redacted]',
      account_id: '[redacted]',
      customer_id: '[redacted]',
      subscription_id: '[redacted]',
      first_name: '[redacted]',
      last_name: '[redacted]',
      name: '[redacted]',
      account_name: '[redacted]',
      npi_number: '[redacted]',
      npi_data: '[redacted]',
      users: Array.isArray(account.users) ? `[${account.users.length} users]` : account.users,
    };
  };
  return {
    ...state,
    session: state.session ? {
      ...state.session,
      token: state.session.token ? '[redacted]' : null,
      ably_token: state.session.ably_token ? '[redacted]' : null,
      auth: state.session.auth ? '[redacted]' : null,
      account: redactAccount(state.session.account),
      customer: state.session.customer ? { id: '[redacted]', object: 'customer' } : null,
    } : state.session,
    authentication: state.authentication ? {
      ...state.authentication,
      user: state.authentication.user ? '[redacted]' : null,
      cognito: state.authentication.cognito ? '[redacted]' : null,
    } : state.authentication,
    contacts: state.contacts ? {
      ...state.contacts,
      list: Array.isArray(state.contacts.list) ? `[${state.contacts.list.length} contacts]` : state.contacts.list,
    } : state.contacts,
    users: state.users ? {
      ...state.users,
      list: Array.isArray(state.users.list) ? `[${state.users.list.length} users]` : state.users.list,
    } : state.users,
    medications: state.medications ? {
      ...state.medications,
      list: Array.isArray(state.medications.list) ? `[${state.medications.list.length} medications]` : state.medications.list,
    } : state.medications,
    transactions: state.transactions ? {
      ...state.transactions,
      list: Array.isArray(state.transactions.list) ? `[${state.transactions.list.length} transactions]` : state.transactions.list,
    } : state.transactions,
    partners: state.partners ? {
      ...state.partners,
      list: Array.isArray(state.partners.list) ? `[${state.partners.list.length} partners]` : state.partners.list,
      invites: Array.isArray(state.partners.invites) ? `[${state.partners.invites.length} invites]` : state.partners.invites,
    } : state.partners,
    plans: state.plans ? {
      ...state.plans,
      list: Array.isArray(state.plans.list) ? `[${state.plans.list.length} plans]` : state.plans.list,
    } : state.plans,
    profiles: state.profiles ? {
      ...state.profiles,
      list: Array.isArray(state.profiles.list) ? `[${state.profiles.list.length} profiles]` : state.profiles.list,
    } : state.profiles,
    accountAssistant: state.accountAssistant ? {
      ...state.accountAssistant,
      query: state.accountAssistant.query ? '[redacted]' : null,
      response: state.accountAssistant.response ? '[redacted]' : null,
      streamingQuery: state.accountAssistant.streamingQuery ? '[redacted]' : null,
      spotlightData: state.accountAssistant.spotlightData ? '[redacted]' : null,
      queries: Array.isArray(state.accountAssistant.queries) ? `[${state.accountAssistant.queries.length} queries]` : state.accountAssistant.queries,
    } : state.accountAssistant,
  };
};

const sanitizeActionForLogRocket = (action) => {
  if (!action) return action;
  // persist/REHYDRATE carries the entire persisted state as its payload —
  // sanitize it the same way we sanitize the state tree.
  if (action.type === 'persist/REHYDRATE' && action.payload) {
    return { ...action, payload: sanitizeStateForLogRocket(action.payload) };
  }
  // Strip raw token/auth payloads from auth actions.
  if (action.type && (
    action.type.includes('TOKEN') ||
    action.type.includes('AUTH') ||
    action.type.includes('LOGIN') ||
    action.type.includes('SESSION')
  )) {
    return { ...action, payload: '[redacted]' };
  }
  return action;
};

let middleware = [thunk, promise, LogRocket.reduxMiddleware({
  stateSanitizer: sanitizeStateForLogRocket,
  actionSanitizer: sanitizeActionForLogRocket,
})];

const store = configureStore({
  preloadedState: defaults,
  reducer: persistedReducer,
  devTools: process.env.REACT_APP_STAGE === "development",
  middleware: (getDefaultMiddleware) =>
    getDefaultMiddleware({
      serializableCheck: false,
      immutableCheck: false,
    }).concat(middleware),
});

const persistor = persistStore(store);

// Function to update persist config with a user-specific salt
const setPersistConfig = (user) => {
  try {
    const userSalt = user?.salt;
    // Skip if no salt, already configured, or bootstrap not ready yet to avoid wiping transient authorized flag
    if (!userSalt) return;
    if (typeof window !== 'undefined') {
      if (window.__PERSIST_CONFIGURED_SALT === userSalt) return; // idempotent
      const phase = window.__BOOTSTRAP_PHASE;
      if (phase !== 'ready') return; // defer until stable
    }
    salt = userSalt;
    localStorage.setItem(SALT_KEY, salt);
    config.doNotEncryptValues = false;
    const newEncryptStorage = new AsyncEncryptStorage(salt, config);
    persistConfig = { ...persistConfig, storage: newEncryptStorage };
    persistedReducer = persistReducer(persistConfig, createRootReducer(history));
    store.replaceReducer(persistedReducer);
    persistor.persist();
    if (typeof window !== 'undefined') window.__PERSIST_CONFIGURED_SALT = userSalt;
  } catch(_) {}
};

export { store, history, persistor, setPersistConfig };